/**
 * Utility functions for schema-scoped storage keys
 * Ensures that localStorage, sessionStorage, cookies, and React Query cache
 * are isolated per VITE_DB_SCHEMA to prevent data contamination between instances
 */

/**
 * Get the current schema from environment variable
 */
export function getSchema(): string {
  return import.meta.env.VITE_DB_SCHEMA || 'public';
}

/**
 * Build a schema-scoped key by prefixing with schema
 * Format: {schema}_{key}
 */
export function buildSchemaKey(key: string): string {
  const schema = getSchema();
  return `${schema}_${key}`;
}

/**
 * Get schema-scoped storage key for localStorage/sessionStorage
 */
export function getSchemaScopedStorageKey(key: string): string {
  return buildSchemaKey(key);
}

/**
 * Get schema-scoped cookie name
 */
export function getSchemaScopedCookieName(name: string): string {
  return buildSchemaKey(name);
}

/**
 * Get schema-scoped React Query query key
 * Adds schema as the first element in the query key array
 */
export function getSchemaScopedQueryKey(key: any[]): any[] {
  const schema = getSchema();
  return [schema, ...key];
}

/**
 * Helper to check if a key is schema-scoped
 */
export function isSchemaScopedKey(key: string): boolean {
  const schema = getSchema();
  return key.startsWith(`${schema}_`);
}

/**
 * Get public URL (domain) for current schema from environment variables
 * Returns the public URL if set via VITE_PUBLIC_URL, otherwise returns null
 * This is optional and used only for reference/validation
 */
export function getPublicUrl(): string | null {
  // Read from environment variable (set at build time)
  const publicUrl = import.meta.env.VITE_PUBLIC_URL;
  return publicUrl || null;
}

/**
 * Get base URL for current schema
 * Always returns '/' as assets are served from root and converted to absolute URLs
 * via VITE_PUBLIC_URL during build process
 */
export function getBaseUrl(): string {
  // Always return '/' - absolute URLs are handled by convertToAbsoluteUrls() plugin
  return '/';
}